/**
 * Environment Service
 *
 * Type-safe environment management service using @monorepo/shared schemas
 */

import {
  type CloneEnvironment,
  type CloneEnvironmentResponse,
  CloneEnvironmentResponseSchema,
  CloneEnvironmentSchema,
  type CreateEnvironment,
  CreateEnvironmentSchema,
  type EnvironmentQuery,
  type EnvironmentResponse,
  EnvironmentResponseSchema,
  type PaginatedEnvironmentsResponse,
  PaginatedEnvironmentsResponseSchema,
  SetDefaultEnvironmentSchema,
  type UpdateEnvironment,
  UpdateEnvironmentSchema,
} from '@monorepo/shared/schemas/environments'
import { del, get, post, put } from '@/lib/api-client'

// Export type alias for easier imports
export type CreateEnvironmentData = CreateEnvironment

export const environmentService = {
  /**
   * Get all environments for current tenant
   */
  async getEnvironments(
    query?: EnvironmentQuery
  ): Promise<PaginatedEnvironmentsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.tenant_id) queryParams.append('tenant_id', query.tenant_id)
    if (query?.type) queryParams.append('type', query.type)
    if (query?.status) queryParams.append('status', query.status)
    if (query?.is_default !== undefined)
      queryParams.append('is_default', query.is_default.toString())
    if (query?.search) queryParams.append('search', query.search)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())
    if (query?.sort) queryParams.append('sort', query.sort)
    if (query?.order) queryParams.append('order', query.order)

    const url = `/environments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedEnvironmentsResponseSchema)
  },

  /**
   * Get single environment by ID
   */
  async getEnvironment(id: string): Promise<EnvironmentResponse> {
    return get(`/environments/${id}`, EnvironmentResponseSchema)
  },

  /**
   * Create new environment
   */
  async createEnvironment(
    data: CreateEnvironment
  ): Promise<EnvironmentResponse> {
    const validated = CreateEnvironmentSchema.parse(data)

    return post('/environments', validated, EnvironmentResponseSchema)
  },

  /**
   * Update environment details
   */
  async updateEnvironment(
    id: string,
    data: UpdateEnvironment
  ): Promise<EnvironmentResponse> {
    const validated = UpdateEnvironmentSchema.parse(data)

    return put(`/environments/${id}`, validated, EnvironmentResponseSchema)
  },

  /**
   * Delete environment (archives it)
   */
  async deleteEnvironment(id: string): Promise<void> {
    await del(`/environments/${id}`)
  },

  /**
   * Switch active environment (client-side only)
   * Validates environment exists and belongs to current tenant, then updates local storage
   */
  async switchEnvironment(environmentId: string): Promise<EnvironmentResponse> {
    // Fetch environment to validate it exists and user has access
    const environment = await this.getEnvironment(environmentId)

    // Update local storage with new environment context
    localStorage.setItem('current_environment_id', environmentId)

    return environment
  },

  /**
   * Set default environment for current tenant (tenant-wide default)
   */
  async setDefaultEnvironment(
    environmentId: string
  ): Promise<EnvironmentResponse> {
    const validated = SetDefaultEnvironmentSchema.parse({
      environment_id: environmentId,
    })

    return post(
      `/environments/${environmentId}/set-default`,
      validated,
      EnvironmentResponseSchema
    )
  },

  /**
   * Set user's default environment for current tenant (user-specific preference)
   */
  async setUserDefaultEnvironment(
    environmentId: string
  ): Promise<{ success: boolean; message: string }> {
    return post('/tenants/user/set-default-environment', {
      environment_id: environmentId,
    })
  },

  /**
   * Clone environment (copy structure and optionally data)
   */
  async cloneEnvironment(
    sourceId: string,
    data: CloneEnvironment
  ): Promise<CloneEnvironmentResponse> {
    const validated = CloneEnvironmentSchema.parse(data)

    return post(
      `/environments/${sourceId}/clone`,
      validated,
      CloneEnvironmentResponseSchema
    )
  },

  /**
   * Get current active environment from localStorage
   */
  getCurrentEnvironmentId(): string | null {
    return localStorage.getItem('current_environment_id')
  },

  /**
   * Clear environment context (used during logout/tenant switch)
   */
  clearEnvironmentContext(): void {
    localStorage.removeItem('current_environment_id')
  },

  /**
   * Generate a URL-friendly slug from a name
   */
  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  },
}
